import { AxiosError } from 'axios';
import { apiClient } from './api';

export interface ApiResponse<T = unknown> {
  error: boolean;
  data?: T;
  status?: number;
  isNetworkError?: boolean;
}

interface ApiRequestOptions {
  requireAuth?: boolean;
  params?: Record<string, unknown>;
}

async function patchAPI(
  endpoint: string,
  body: unknown,
  options: ApiRequestOptions = {}
): Promise<ApiResponse> {
  try {
    const config =
      options.requireAuth === false
        ? { transformRequest: [(data: unknown) => data] }
        : {};
    const response = await apiClient.patch(endpoint, body, config);
    return { error: false, data: response.data, status: response.status };
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401 && options.requireAuth !== false) {
      return new Promise(() => {});
    }
    const response: ApiResponse = {
      error: true,
      data: axiosError.response?.data || axiosError.message,
      isNetworkError: !axiosError.response && !!axiosError.request,
    };
    if (axiosError.response?.status) {
      response.status = axiosError.response.status;
    }
    return response;
  }
}

async function getAPI(
  endpoint: string,
  options: ApiRequestOptions = {}
): Promise<ApiResponse> {
  try {
    const config =
      options.requireAuth === false
        ? {
            params: options.params,
            transformRequest: [(data: unknown) => data],
          }
        : { params: options.params };
    const response = await apiClient.get(endpoint, config);
    return { error: false, data: response.data, status: response.status };
  } catch (error) {
    const axiosError = error as AxiosError;
    const status = axiosError.response?.status;
    if (status === 401 && options.requireAuth !== false) {
      return new Promise(() => {});
    }
    const response: ApiResponse = {
      error: true,
      data: axiosError.response?.data || axiosError.message,
      isNetworkError: !axiosError.response && !!axiosError.request,
    };
    if (status) {
      response.status = status;
    }
    return response;
  }
}

async function postAPI(
  endpoint: string,
  body: unknown,
  options: ApiRequestOptions = {}
): Promise<ApiResponse> {
  try {
    const config =
      options.requireAuth === false
        ? {
            transformRequest: [
              (data: unknown) =>
                endpoint.includes('forgot-password') ||
                endpoint.includes('verify-token') ||
                endpoint.includes('reset-password')
                  ? JSON.stringify(data)
                  : data,
            ],
          }
        : {};
    const response = await apiClient.post(endpoint, body, config);
    return { error: false, data: response.data, status: response.status };
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401 && options.requireAuth !== false) {
      return new Promise(() => {});
    }
    const response: ApiResponse = {
      error: true,
      data: axiosError.response?.data || axiosError.message,
      isNetworkError: !axiosError.response && !!axiosError.request,
    };
    if (axiosError.response?.status) {
      response.status = axiosError.response.status;
    }
    return response;
  }
}

async function putAPI(
  endpoint: string,
  body: unknown,
  options: ApiRequestOptions = {}
): Promise<ApiResponse> {
  try {
    const config =
      options.requireAuth === false
        ? { transformRequest: [(data: unknown) => data] }
        : {};
    const response = await apiClient.put(endpoint, body, config);
    return { error: false, data: response.data, status: response.status };
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401 && options.requireAuth !== false) {
      return new Promise(() => {});
    }
    const response: ApiResponse = {
      error: true,
      data: axiosError.response?.data || axiosError.message,
      isNetworkError: !axiosError.response && !!axiosError.request,
    };
    if (axiosError.response?.status) {
      response.status = axiosError.response.status;
    }
    return response;
  }
}

async function deleteAPI(
  endpoint: string,
  body?: unknown,
  options: ApiRequestOptions = {}
): Promise<ApiResponse> {
  try {
    const config =
      options.requireAuth === false
        ? {
            data: body,
            transformRequest: [(data: unknown) => data],
          }
        : { data: body };
    const response = await apiClient.delete(endpoint, config);
    return { error: false, data: response.data, status: response.status };
  } catch (error) {
    const axiosError = error as AxiosError;
    if (axiosError.response?.status === 401 && options.requireAuth !== false) {
      return new Promise(() => {});
    }
    const response: ApiResponse = {
      error: true,
      data: axiosError.response?.data || axiosError.message,
      isNetworkError: !axiosError.response && !!axiosError.request,
    };
    if (axiosError.response?.status) {
      response.status = axiosError.response.status;
    }
    return response;
  }
}

export const apiService = {
  patchAPI,
  putAPI,
  getAPI,
  postAPI,
  deleteAPI,
};

export const publicApiService = {
  patchAPI: (endpoint: string, body: unknown) =>
    patchAPI(endpoint, body, { requireAuth: false }),
  getAPI: (endpoint: string) => getAPI(endpoint, { requireAuth: false }),
  postAPI: (endpoint: string, body: unknown) =>
    postAPI(endpoint, body, { requireAuth: false }),
  deleteAPI: (endpoint: string, body?: unknown) =>
    deleteAPI(endpoint, body, { requireAuth: false }),
};
